import { RedoButton } from "@redotech/arbiter/buttons/redo-button";
import { memo } from "react";
import type { OverallProgress } from "../../registry/types";
import { computeProgressPercentage } from "../widget/widget-progress-bar";
import * as css from "./setup-guide-header-tab.module.css";

export interface SetupGuideHeaderTabProps {
  productDisplayName: string;
  progress: OverallProgress;
  onClick: () => void;
}

// Donut geometry — small enough to feel like a glyph next to the
// "Setup guide" label. r + sw/2 must clear the 16px viewBox bounds.
const DONUT_RADIUS = 6;
const DONUT_CIRCUMFERENCE = 2 * Math.PI * DONUT_RADIUS;

/**
 * Inline pill button shown in the top nav when the widget is collapsed
 * — same dark-mode RedoButton recipe as the Ask Redo button, with a
 * tiny donut-chart "icon" that fills as setup tasks complete.
 */
export const SetupGuideHeaderTab = memo(function SetupGuideHeaderTab({
  productDisplayName,
  progress,
  onClick,
}: SetupGuideHeaderTabProps) {
  const percentage = computeProgressPercentage(
    progress.complete,
    progress.total,
  );
  const dashOffset = DONUT_CIRCUMFERENCE * (1 - percentage / 100);

  // Closure-style icon component so the donut can read the current
  // dashOffset. Mirrors the inline `OrangeAiStarIcon` pattern used by
  // the Ask Redo button next to it.
  const DonutIcon = () => (
    <svg
      aria-hidden
      className={css.donut}
      viewBox="0 0 16 16"
      xmlns="http://www.w3.org/2000/svg"
    >
      <circle className={css.donutTrack} cx="8" cy="8" r={DONUT_RADIUS} />
      <circle
        className={css.donutFill}
        cx="8"
        cy="8"
        r={DONUT_RADIUS}
        strokeDasharray={DONUT_CIRCUMFERENCE}
        strokeDashoffset={dashOffset}
      />
    </svg>
  );

  return (
    <RedoButton
      aria-label={`Open ${productDisplayName} setup guide (${percentage}% complete)`}
      buttonClassName={css.button}
      className="dark-mode"
      hierarchy="secondary"
      IconLeading={DonutIcon}
      onClick={onClick}
      size="sm"
      text="Setup guide"
    />
  );
});
